import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { EXPERIENCE_STATS } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { trackPageLifecycle } from '../lib/customerEventTracker';

const buildAboutPanels = (t) => [
  { id: 'salaried', title: t('about_page.panel_salaried_title', "Salaried? Don't pay extra tax"), desc: t('about_page.panel_salaried_desc', ''), cta: t('about_page.panel_salaried_cta', 'Plan & Save'), to: '/file-itr' },
  { id: 'lastminute', title: t('about_page.panel_lastminute_title', 'Tired of last-minute tax filing'), desc: t('about_page.panel_lastminute_desc', ''), cta: t('about_page.panel_lastminute_cta', 'File On Time'), to: '/get-started' },
  { id: 'notice', title: t('about_page.panel_notice_title', "Got a tax notice? Don't panic"), desc: t('about_page.panel_notice_desc', ''), cta: t('about_page.panel_notice_cta', 'Resolve Notice'), to: '/file-itr?service=ITR_NOTICE' },
  { id: 'whychoose', title: t('about_page.panel_why_title', 'Why choose Solve Tax'), desc: t('about_page.panel_why_desc', ''), cta: t('about_page.panel_why_cta', 'Talk to Expert'), to: '/contact' },
];

const MarketingPanelCard = ({ panel }) => (
  <Link to={panel.to} className="card-lift block rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm group h-full">
    <div className="p-5">
      <h3 className="text-base font-bold text-[#0f2540] mb-2">{panel.title}</h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4">{panel.desc}</p>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#14A098]">
        {panel.cta} <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

const EXP_KEYS = ['years', 'clients', 'returns', 'regs'];

const About = () => {
  const { t } = useLanguage();
  const panels = useMemo(() => buildAboutPanels(t), [t]);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'ABOUT_PAGE',
      form_name: 'AboutPage',
      service_code: null,
      open_event_name: 'about_page_open',
    }),
    [],
  );

  return (
    <div className="pt-22">
      <section className="py-6 hero-gradient">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">
            {t('about.title_about', 'About')} <span className="text-[#14A098]">{t('about.title_brand', 'SolveTax')}</span>
          </h1>
          <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto">
            {t('about_page.hero_sub', "From the headache of tax filing to last-minute deadlines, scary notices and smart savings — SolveTax has you covered. Here's how we help India breathe easy.")}
          </p>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {panels.map((panel) => <MarketingPanelCard key={panel.id} panel={panel} />)}
          </div>
        </div>
      </section>

      <section className="py-8 bg-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-[#0f2540] to-[#14A098] p-7 md:p-10 shadow-xl">
            <h3 className="text-white text-2xl font-bold mb-6">{t('about_page.exp_title', 'Our Experience')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {EXPERIENCE_STATS.map((s, i) => (
                <div key={s.label} className="text-white">
                  <div className="text-3xl md:text-4xl font-extrabold mb-2">{s.value}</div>
                  <div className="text-sm text-teal-100">{t(`exp.${EXP_KEYS[i] || 'years'}`, s.label)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0f2540] mb-4">{t('about_page.cta_title', 'Ready to work with us?')}</h2>
          <p className="text-gray-600 mb-6">{t('about_page.cta_sub', 'File your Income Tax Return or get a free consultation today.')}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/file-itr"><Button size="lg" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('about_page.cta_file', 'File ITR Now')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-[#14A098] text-[#14A098] rounded-full">{t('about_page.cta_contact', 'Contact Us')}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
